import React from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, Lightbulb, Package2, Sparkles } from 'lucide-react';
import { processOptions, substrateOptions } from '../films/FilmWizard';
import { SEALING_MACHINE_IDS } from '../../utils/sealingCompatibility';
import { GUIDANCE_WIZARD_STEPS } from '../../hooks/useGuidanceState';

const stepLabels = {
  productType: 'Product Type',
  portionSize: 'Portion Size',
  compartments: 'Compartments',
  useCase: 'Use Case',
  film: 'Film Fit',
  summary: 'Suggestions'
};

const gradientStyle = {
  background:
    'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
};

const GuidanceWizard = ({
  state,
  onNavigateToSection,
  onToggleContainerSelection,
  onToggleDeliSelection,
  onToggleFilmSelection,
  selectedContainers,
  selectedDeliItems,
  selectedFilmItems
}) => {
  const {
    answers,
    containerMatches,
    containerRecommendationReason,
    containerType,
    containerTypeMeta,
    currentQuestion,
    currentStepId,
    filmProcesses,
    filmSubstrate,
    goBack,
    goForward,
    handleSelectAnswer,
    nextButtonEnabled,
    recommendedContainerHeadline,
    recommendedFilms,
    setFilmSubstrate,
    shouldHighlightDeliFilm,
    incompatibleSealingMachineIds,
    toggleFilmProcess,
    resetWizard,
    summaryVersion
  } = state;

  const currentStepIndex = GUIDANCE_WIZARD_STEPS.indexOf(currentStepId);

  const renderStepIndicator = () => {
    return (
      <div className="flex flex-wrap gap-3 justify-center mb-8">
        {GUIDANCE_WIZARD_STEPS.map((step, index) => {
          const isActive = step === currentStepId;
          const isCompleted = currentStepIndex > index;
          return (
            <div
              key={step}
              className={`flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-semibold ${
                isActive
                  ? 'border-transparent text-white shadow-md'
                  : isCompleted
                  ? 'border-orange-300 text-orange-600 bg-orange-50'
                  : 'border-gray-300 text-gray-600 bg-white'
              }`}
              style={isActive ? gradientStyle : undefined}
            >
              <span className="w-6 h-6 rounded-full flex items-center justify-center border border-current">
                {index + 1}
              </span>
              <span>{stepLabels[step]}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderQuestionStep = () => {
    if (!currentQuestion) return null;

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-10 border border-gray-100">
        <h2 className="text-3xl md:text-4xl font-bold uppercase mb-4 text-gray-900">{currentQuestion.title}</h2>
        <p className="text-lg text-gray-600 mb-8">
          Choose the option that best describes your product. You can always adjust later.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentQuestion.options.map((option) => {
            const isSelected = answers[currentQuestion.id] === option.value;
            return (
              <button
                key={option.value}
                className={`p-6 rounded-lg border-2 text-left transition-colors ${
                  isSelected
                    ? 'text-white border-transparent shadow-lg'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-orange-300'
                }`}
                type="button"
                onClick={() => handleSelectAnswer(currentQuestion.id, option.value)}
                style={isSelected ? gradientStyle : undefined}
              >
                <span className="block text-xl font-semibold mb-2">{option.label}</span>
                <span className="text-sm text-current/90 leading-relaxed">{option.description}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFilmStep = () => {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-10 border border-gray-100">
        <h2 className="text-3xl md:text-4xl font-bold uppercase mb-4 text-gray-900">
          Match Films to Your Container
        </h2>
        <p className="text-lg text-gray-600 mb-6">
          Select all of the processes your package needs to withstand, then choose the container substrate.
        </p>

        <div className="mb-8">
          <h3 className="text-2xl font-semibold text-gray-800 mb-4">Processes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {processOptions.map((process) => {
              const isSelected = filmProcesses.includes(process);
              return (
                <button
                  key={process}
                  type="button"
                  className={`p-5 rounded-lg border-2 text-center text-lg font-semibold transition-colors ${
                    isSelected
                      ? 'text-white border-transparent shadow-lg'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-orange-300'
                  }`}
                  style={isSelected ? gradientStyle : undefined}
                  onClick={() => toggleFilmProcess(process)}
                >
                  {process}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-4">Container Substrate</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {substrateOptions.map((substrate) => {
              const isSelected = filmSubstrate === substrate;
              return (
                <button
                  key={substrate}
                  type="button"
                  className={`p-5 rounded-lg border-2 text-center text-lg font-semibold transition-colors ${
                    isSelected
                      ? 'text-white border-transparent shadow-lg'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-orange-300'
                  }`}
                  style={isSelected ? gradientStyle : undefined}
                  onClick={() => setFilmSubstrate(substrate)}
                >
                  {substrate}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderContainerCard = (item, isMealContainer) => {
    if (isMealContainer) {
      const isSelected = selectedContainers.includes(item.idString);
      return (
        <div
          key={item.idString}
          className={`rounded-lg border-2 p-5 flex flex-col gap-3 transition ${
            isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:shadow-md'
          }`}
        >
          <div className="flex items-center gap-3">
            <Package2 className="text-orange-500" size={24} />
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{item.collection}</h4>
              <p className="text-sm text-gray-600">#{item.idString}</p>
            </div>
          </div>
          <div className="text-sm text-gray-700 grid grid-cols-1 gap-1">
            {item.volumeOz && <span>Volume: {item.volumeOz} oz</span>}
            {item.compartments && <span>Compartments: {item.compartments}</span>}
            {item.material && <span>Material: {item.material}</span>}
            {item.dimensions && <span>Dimensions: {item.dimensions}</span>}
          </div>
          <button
            type="button"
            onClick={() => onToggleContainerSelection(item.idString)}
            className={`mt-auto text-sm font-semibold px-4 py-2 rounded-md border-2 transition ${
              isSelected
                ? 'border-orange-500 text-orange-600 bg-white'
                : 'border-gray-200 text-gray-700 hover:border-orange-300'
            }`}
          >
            {isSelected ? 'Remove from Kit' : 'Add to Kit'}
          </button>
        </div>
      );
    }

    const isSelected = selectedDeliItems.includes(item['Item #']);
    return (
      <div
        key={item['Item #']}
        className={`rounded-lg border-2 p-5 flex flex-col gap-3 transition ${
          isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:shadow-md'
        }`}
      >
        <div className="flex items-center gap-3">
          <Package2 className="text-orange-500" size={24} />
          <div>
            <h4 className="text-lg font-semibold text-gray-900">{item.Description}</h4>
            <p className="text-sm text-gray-600">Item #{item['Item #']}</p>
          </div>
        </div>
        <div className="text-sm text-gray-700 grid grid-cols-1 gap-1">
          {item['Fluid oz'] && <span>Volume: {item['Fluid oz']} oz</span>}
          {item.Dimensions && <span>Dimensions: {item.Dimensions}</span>}
          {item.Availability && <span>Availability: {item.Availability}</span>}
        </div>
        <button
          type="button"
          onClick={() => onToggleDeliSelection(item['Item #'])}
          className={`mt-auto text-sm font-semibold px-4 py-2 rounded-md border-2 transition ${
            isSelected
              ? 'border-orange-500 text-orange-600 bg-white'
              : 'border-gray-200 text-gray-700 hover:border-orange-300'
          }`}
        >
          {isSelected ? 'Remove from Kit' : 'Add to Kit'}
        </button>
      </div>
    );
  };

  const renderSummaryStep = () => {
    const isMeal = containerType !== 'deli';
    const groups = containerMatches.groups || [];
    const showSealingMachineNotice = Boolean(
      incompatibleSealingMachineIds &&
        incompatibleSealingMachineIds.has(SEALING_MACHINE_IDS.CTS_528XL)
    );
    return (
      <div className="space-y-8">
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-6 flex items-start gap-4">
          <Lightbulb className="text-orange-500" size={32} />
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Here’s Your Best Starting Point</h3>
            <p className="text-gray-700">{containerTypeMeta.description}</p>
            {containerRecommendationReason.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-gray-700">
                {containerRecommendationReason.map((reason) => (
                  <li key={reason}>• {reason}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 md:p-10 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-3xl font-bold text-gray-900">{recommendedContainerHeadline}</h3>
              <p className="text-gray-600">Top matches based on your answers.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateToSection('containers')}
              className="text-sm font-semibold px-4 py-2 rounded-md border-2 border-orange-500 text-orange-600 hover:bg-orange-50 transition"
            >
              View All Containers
            </button>
          </div>

          {showSealingMachineNotice && (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-gray-800">
              <Package2 className="text-red-500" size={20} />
              <div>
                <p className="font-semibold text-red-700">Sealing machine note</p>
                <p>
                  The CTS-528XL Deli Container Sealing Machine is not compatible with the highlighted 8.5×6.5 trays, so it has been removed from these recommendations.
                </p>
              </div>
            </div>
          )}

          {groups.length === 0 ? (
            <p className="text-gray-600">We couldn’t find an exact match. Open the full catalog to explore every option.</p>
          ) : (
            <div className="space-y-6">
              {groups.map((group) => (
                <div key={group.collection}>
                  {group.collection && (
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">{group.collection}</h4>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.items.map((item) => renderContainerCard(item, isMeal))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 md:p-10 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-3xl font-bold text-gray-900">Compatible Films</h3>
              <p className="text-gray-600">Pair these films with your recommended containers for a complete solution.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateToSection('films')}
              className="text-sm font-semibold px-4 py-2 rounded-md border-2 border-orange-500 text-orange-600 hover:bg-orange-50 transition"
            >
              View All Films
            </button>
          </div>

          {shouldHighlightDeliFilm && (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-gray-800">
              <Sparkles className="text-orange-500" size={20} />
              <div>
                <p className="font-semibold text-orange-700">Recommended film: 528XL (optimized for deli applications)</p>
                <p>
                  This film delivers a perfect seal and clarity for deli containers. We recommend adding it to your kit
                  alongside the containers above.
                </p>
              </div>
            </div>
          )}

          {recommendedFilms.length === 0 ? (
            <p className="text-gray-600">Select at least one process to generate tailored film recommendations.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendedFilms.slice(0, 6).map((film) => {
                const isSelected = selectedFilmItems.includes(film.id);
                return (
                  <div
                    key={film.id}
                    className={`border-2 rounded-lg p-5 flex flex-col gap-3 transition ${
                      isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-xl font-semibold text-gray-900">{film.name}</h4>
                        <p className="text-sm text-gray-600">{film.mil} mil • {film.micron} micron</p>
                      </div>
                      {isSelected && <CheckCircle className="text-orange-500" size={20} />}
                    </div>
                    <div className="text-sm text-gray-700 grid grid-cols-1 gap-1">
                      <span>
                        Processes: {
                          ['microwavable', 'ovenable', 'freezable']
                            .filter((key) => film[key])
                            .join(', ') || 'General Purpose'
                        }
                      </span>
                      <span>
                        Substrates: {
                          [film.pp && 'PP', film.apetPet && 'APET/PET', film.cpet && 'CPET', film.pe && 'PE', film.paper && 'Paper']
                            .filter(Boolean)
                            .join(', ') || 'General'
                        }
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onToggleFilmSelection(film.id)}
                      className={`mt-auto text-sm font-semibold px-4 py-2 rounded-md border-2 transition ${
                        isSelected
                          ? 'border-orange-500 text-orange-600 bg-white'
                          : 'border-gray-200 text-gray-700 hover:border-orange-300'
                      }`}
                    >
                      {isSelected ? 'Remove from Kit' : 'Add to Kit'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (currentStepId === 'film') {
      return renderFilmStep();
    }
    if (currentStepId === 'summary') {
      return <div key={summaryVersion}>{renderSummaryStep()}</div>;
    }
    return renderQuestionStep();
  };

  const renderFooterControls = () => {
    if (currentStepId === 'summary') {
      return (
        <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-2 px-5 py-3 border-2 border-gray-300 text-gray-700 rounded-md font-semibold hover:bg-gray-50 transition"
            >
              <ArrowLeft size={18} />
              Back
            </button>
            <button
              type="button"
              onClick={resetWizard}
              className="inline-flex items-center gap-2 px-5 py-3 border-2 border-orange-500 text-orange-600 rounded-md font-semibold hover:bg-orange-50 transition"
            >
              Restart Questionnaire
            </button>
          </div>
          <button
            type="button"
            onClick={() => onNavigateToSection('kit-review')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-white font-semibold shadow-lg"
            style={gradientStyle}
          >
            Review My Kit
          </button>
        </div>
      );
    }

    return (
      <div className="mt-8 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <button
          type="button"
          onClick={goBack}
          disabled={currentStepId === GUIDANCE_WIZARD_STEPS[0]}
          className={`inline-flex items-center gap-2 px-5 py-3 border-2 rounded-md font-semibold transition ${
            currentStepId === GUIDANCE_WIZARD_STEPS[0]
              ? 'border-gray-200 text-gray-300 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <button
          type="button"
          disabled={!nextButtonEnabled}
          onClick={goForward}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-md text-white font-semibold shadow-lg transition ${
            nextButtonEnabled ? '' : 'opacity-60 cursor-not-allowed'
          }`}
          style={nextButtonEnabled ? gradientStyle : undefined}
        >
          Next
          <ArrowRight size={18} />
        </button>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <header className="text-center mb-12 space-y-4">
        <div className="inline-flex items-center gap-3 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-orange-600 font-semibold uppercase text-sm tracking-wider">
          <Sparkles size={16} />
          Guided Packaging Selection
        </div>
        <h1 className="text-5xl font-bold uppercase text-brandOrange">Find the Perfect Containers & Films</h1>
        <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
          Answer a few tailored questions and we’ll match the ideal containers and sealing films for your product.
        </p>
      </header>

      {renderStepIndicator()}

      {renderContent()}

      {renderFooterControls()}
    </div>
  );
};

export default GuidanceWizard;
